import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  PefApiData,
  PefCell,
  PefRow,
  PefSheet,
} from '@sportello-balneari/api-client';

/**
 * The PEF wire contract lives in `@sportello-balneari/api-client` so the API
 * client does not depend on this UI library. Re-exported here so existing
 * `from '@sportello-balneari/pef-editor'` imports keep working.
 */
export * from '@sportello-balneari/api-client';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

/**
 * Flat map of all cell values used as the editor's internal state.
 * - Column-based cells: key is `${sheetId}.${rowId}.${colId}` (3 levels).
 * - Scalar cells (no column dimension): key is `${sheetId}.${rowId}` (2 levels).
 */
export type PefData = Record<string, number | string | null>;

export function flattenPefApiData(apiData: PefApiData): PefData {
  const result: PefData = {};
  if (!apiData) return result;

  function walk(node: unknown, prefix: string): void {
    if (node === null || node === undefined) {
      result[prefix] = node as null;
    } else if (Array.isArray(node)) {
      node.forEach((item, i) => walk(item, `${prefix}.${i}`));
    } else if (typeof node === 'object') {
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        walk(v, `${prefix}.${k}`);
      }
    } else {
      result[prefix] = node as number | string;
    }
  }

  for (const [k, v] of Object.entries(apiData)) {
    walk(v, k);
  }
  return result;
}

/**
 * Converts the editor's flat {@link PefData} map back into the nested
 * {@link PefApiData} structure expected by the API.
 *
 * Dot-separated keys are expanded into nested objects or arrays.
 * A path segment is treated as an **array index** when it is a non-negative
 * integer whose value is < 1000 (distinguishes item indices like `0`, `1`, `12`
 * from year-like keys such as `2026`).
 */
export function structurePefApiData(flat: PefData): PefApiData {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(flat)) {
    setDeepValue(result, key.split('.'), val);
  }
  return result as PefApiData;
}

function isArrayIndex(segment: string): boolean {
  return /^\d+$/.test(segment) && Number(segment) < 1000;
}

function setDeepValue(
  container: Record<string, unknown> | unknown[],
  parts: string[],
  val: unknown,
): void {
  const [head, ...rest] = parts;

  if (rest.length === 0) {
    if (Array.isArray(container)) {
      (container as unknown[])[Number(head)] = val;
    } else {
      (container as Record<string, unknown>)[head] = val;
    }
    return;
  }

  // Determine the type of container needed for the next level
  const nextIsArray = isArrayIndex(rest[0]);

  let child: unknown;
  if (Array.isArray(container)) {
    const idx = Number(head);
    if (container[idx] === undefined || container[idx] === null) {
      container[idx] = nextIsArray ? [] : {};
    }
    child = container[idx];
  } else {
    const obj = container as Record<string, unknown>;
    if (obj[head] === undefined || obj[head] === null) {
      obj[head] = nextIsArray ? [] : {};
    }
    child = obj[head];
  }

  setDeepValue(child as Record<string, unknown> | unknown[], rest, val);
}

// ---------------------------------------------------------------------------
// Cell resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the effective PefCell for a given sheet row + column.
 * Merges cellTemplate (row-level default) with any column-specific override.
 * Falls back to an empty cell if neither is defined.
 */
export function resolvePefCell(
  sheet: PefSheet,
  row: PefRow,
  columnId: string,
): PefCell {
  const colDef = sheet.columns.find((c) => c.id === columnId);
  if (colDef?.standalone && !row.cells?.[columnId]) return { type: 'empty' };

  if (row.startColumn && !row.cells?.[columnId]) {
    const startIdx = sheet.columns.findIndex((c) => c.id === row.startColumn);
    const colIdx = sheet.columns.findIndex((c) => c.id === columnId);
    if (startIdx !== -1 && colIdx < startIdx) return { type: 'empty' };
  }

  const template = row.cellTemplate ?? {};
  const override = row.cells?.[columnId] ?? {};
  const merged = { ...template, ...override } as PefCell;
  if (!merged.type) merged.type = 'empty';

  if (
    (merged.type === 'input' || merged.type === 'calculated') &&
    !merged.valueKey
  ) {
    merged.valueKey = `${sheet.id}.${row.id}.${columnId}`;
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export interface PefLoadResult {
  /** Full computed dataset; the editor flattens it into serverData */
  data: PefApiData;
  /** Previously saved user inputs; pre-populate cells without marking them dirty */
  savedUserInputs?: PefApiData;
}

export interface PefAdapter {
  load(): Observable<PefLoadResult>;
  calculate(userInputs: PefApiData): Observable<PefApiData>;
}

export const PEF_ADAPTER = new InjectionToken<PefAdapter>('PEF_ADAPTER');
